/**
 * Revealing a bubble's hover chrome — the action bar and the smiley-plus — without
 * breaking the tooltips that hang off it.
 *
 * Two rules, and both are about the same thing: a Radix tooltip anchors to its TRIGGER,
 * and a trigger that stops existing takes the tooltip's anchor with it.
 *
 * ## 1. Reveal with `visibility`, never `display`
 *
 * `hidden` collapses the trigger's box to nothing. The tooltip does not unmount in that
 * moment — it is mid close animation, and Radix leaves `hideWhenDetached` off, so
 * Floating UI keeps positioning it against a reference whose rect is now 0x0 at the
 * document origin. The tooltip flies to the top-left corner while it fades. `invisible`
 * keeps the box exactly where it was, so the exit plays under the pointer where the
 * pointer left it.
 *
 * This is the pattern the kanban column's Collapse button already uses
 * (`features/office/orders/list/main/kanban-view/_components/kanban-column-header.tsx`),
 * for the same reason. The cost is that the chrome always occupies its space, and in both
 * places that cost is nothing: the conversation's action bar is absolutely positioned, so
 * it is out of flow whether it is visible or not, and the reaction row's expander holds a
 * fixed box ON PURPOSE — see `channel-message-reaction-footer.tsx`, where the reservation
 * is what keeps the overflow measurement from oscillating.
 *
 * ## 2. An open tooltip counts as hovering the message
 *
 * `TooltipContent` is portaled to the body, so the pointer crossing from a button onto its
 * own tooltip leaves the message's `group` — `BubbleBox`, the bubble's own box. Revealed on
 * `group-hover` alone, the chrome would disappear under the pointer and the bar would
 * re-measure around the gap. The state lives on the trigger, which stays inside the group
 * even while its content is portaled out, and Radix spells it `delayed-open` or
 * `instant-open` — never a plain `open`, which belongs to a popover and is covered below.
 *
 * ## What this does NOT cover
 *
 * An open MENU or PICKER. Both of those triggers are also a tooltip trigger, and two
 * `asChild` Radix triggers on one button settle `data-state` in the outer one's favour — so
 * the button reads `closed` while its menu is open, and no `data-state` rule can see it.
 * The conversation bar holds itself up with `aria-expanded`, which the tooltip never sets;
 * see `channel-message-action-bar.tsx`.
 *
 * One literal rather than a builder: Tailwind generates only the classes it can read in
 * the source, so a class assembled from a template literal never exists.
 */

/**
 * Reveals chrome that is `invisible` at rest. Pair it with `invisible` on the same
 * element — never with `hidden`, which is the bug above.
 */
export const MESSAGE_HOVER_VISIBLE =
  "group-hover:visible group-has-data-[state=delayed-open]:visible group-has-data-[state=instant-open]:visible";
